import { useEffect, useRef, useState } from "react"
import {
  FileCode2,
  Hammer,
  MoreVertical,
  Pencil,
  ScrollText,
  SlidersHorizontal,
  Trash2,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../DropdownMenu/DropdownMenu"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../Sheet/Sheet"
import {
  PublishedNoteEditor,
  PublishedNoteEditorContent,
} from "../PublishedNoteEditor/PublishedNoteEditor"
import {
  DeploymentStatusBadge,
  DeploymentSymbolPill,
  DeploymentTimeframePill,
} from "../DeploymentPills/DeploymentPills"
import type { DeploymentGroup, PublishedDeployment } from "../types/deployment"

// Version gets room for its number plus the "Multiple positions" badge; below that width the
// version cell wraps the badge onto its own line instead of running under the Symbol column.
const DESKTOP_GRID_COLS =
  "grid-cols-[26%_16%_11%_9%_18%_16%_56px]"

export interface DeploymentTableLabels {
  notes: string
  version: string
  symbol: string
  timeframe: string
  deployed: string
  status: string
  strategyActionsAriaLabel: string
  deleteAllVersions: string
  codeAction: string
  settingsAction: string
  logsAction: string
  buildAction: string
  deleteAction: string
  rowActionsAriaLabel: (entry: PublishedDeployment) => string
  mobileActionsTitle: (entry: PublishedDeployment) => React.ReactNode
}

const DEFAULT_LABELS: DeploymentTableLabels = {
  notes: "Notes",
  version: "Version",
  symbol: "Symbol",
  timeframe: "TF",
  deployed: "Deployed",
  status: "Status",
  strategyActionsAriaLabel: "Strategy actions",
  deleteAllVersions: "Delete all versions",
  codeAction: "Code",
  settingsAction: "Settings",
  logsAction: "Logs",
  buildAction: "Build",
  deleteAction: "Delete",
  rowActionsAriaLabel: (entry) => `${entry.ver} actions`,
  mobileActionsTitle: (entry) => (
    <>
      <span className="font-mono tabular-nums">{entry.ver}</span>
      <span className="text-caption text-muted-foreground font-normal">Actions</span>
    </>
  ),
}

export interface StrategyDeploymentTableProps {
  group: DeploymentGroup
  strategyName: string
  isEntryRunning?: (entry: PublishedDeployment) => boolean
  recentlyDeployedId?: number | string | null
  onDeployHighlightEnd?: () => void
  onOpenCode?: (entry: PublishedDeployment) => void
  onOpenParams?: (entry: PublishedDeployment) => void
  onShowLogs?: (entry: PublishedDeployment) => void
  onBuild?: (entry: PublishedDeployment) => void
  onUndeployVersion?: (entry: PublishedDeployment) => void
  onUndeployAll?: (group: DeploymentGroup) => void
  onNoteChange?: (entry: PublishedDeployment, note: string) => void
  formatMobileDate?: (date: string) => string
  /** Optional slot rendered immediately before the per-row action menu (e.g. Start/Stop button on desktop). */
  renderRowAction?: (entry: PublishedDeployment) => React.ReactNode
  /** Optional slot rendered beside the version cell (e.g. "Draft" pill on desktop). */
  renderVersionBadge?: (entry: PublishedDeployment) => React.ReactNode
  /**
   * Optional slot rendered directly below each per-version row (e.g. live MT5 connection
   * status on desktop). Returning null/undefined skips the extra row entirely.
   */
  renderRowDetails?: (entry: PublishedDeployment) => React.ReactNode
  labels?: Partial<DeploymentTableLabels>
  className?: string
}

function useDeployHighlight(isTarget: boolean, onEnd?: () => void) {
  const ref = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!isTarget) return
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        observer.disconnect()
        el.classList.add("animate-deploy-row-highlight")
        const handleEnd = () => {
          el.classList.remove("animate-deploy-row-highlight")
          onEnd?.()
        }
        el.addEventListener("animationend", handleEnd, { once: true })
      },
      { threshold: 0.5 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isTarget, onEnd])
  return ref
}

export function StrategyDeploymentTable({
  group,
  strategyName,
  isEntryRunning,
  recentlyDeployedId,
  onDeployHighlightEnd,
  onOpenCode,
  onOpenParams,
  onShowLogs,
  onBuild,
  onUndeployVersion,
  onUndeployAll,
  onNoteChange,
  formatMobileDate,
  renderRowAction,
  renderVersionBadge,
  renderRowDetails,
  labels: labelsOverride,
  className,
}: StrategyDeploymentTableProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsOverride }
  const { all } = group

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-card shadow-md overflow-hidden",
        className,
      )}
    >
      <div className="px-4 sm:px-5 py-2.5 sm:py-3.5 border-b border-border flex items-center justify-between gap-3">
        <h3 className="text-section text-foreground truncate min-w-0">{strategyName}</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 sm:h-7 sm:w-7 p-0 [&_svg]:size-4 sm:[&_svg]:size-3.5 text-muted-foreground hover:text-foreground shrink-0"
              aria-label={labels.strategyActionsAriaLabel}
            >
              <MoreVertical size={14} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="v1 w-48">
            <DropdownMenuItem
              onSelect={() => onUndeployAll?.(group)}
              className="text-caption"
            >
              <Trash2 size={12} strokeWidth={1.75} />
              {labels.deleteAllVersions}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="sm:hidden flex flex-col divide-y divide-border">
        {all.map((entry, i) => (
          <VersionMobileCard
            key={entry.id}
            entry={entry}
            isLatest={i === 0}
            running={isEntryRunning?.(entry) ?? false}
            isRecentlyDeployed={entry.id === recentlyDeployedId}
            onHighlightEnd={onDeployHighlightEnd}
            labels={labels}
            formatMobileDate={formatMobileDate}
            rowAction={renderRowAction?.(entry)}
            versionBadge={renderVersionBadge?.(entry)}
            rowDetails={renderRowDetails?.(entry)}
            onOpenCode={() => onOpenCode?.(entry)}
            onBuild={() => onBuild?.(entry)}
            onOpenParams={() => onOpenParams?.(entry)}
            onShowLogs={onShowLogs ? () => onShowLogs(entry) : undefined}
            onUndeploy={() => onUndeployVersion?.(entry)}
            onNoteChange={onNoteChange ? (next) => onNoteChange(entry, next) : undefined}
          />
        ))}
      </div>

      <div role="table" className="hidden sm:block bg-muted/20 text-caption">
        <div
          role="row"
          className={cn(
            "grid items-center bg-muted/30",
            DESKTOP_GRID_COLS,
          )}
        >
          <div
            role="columnheader"
            className="px-5 py-2.5 text-caption uppercase tracking-wider text-muted-foreground"
          >
            {labels.notes}
          </div>
          <div
            role="columnheader"
            className="px-5 py-2.5 text-caption uppercase tracking-wider text-muted-foreground"
          >
            {labels.version}
          </div>
          <div
            role="columnheader"
            className="px-5 py-2.5 text-caption uppercase tracking-wider text-muted-foreground"
          >
            {labels.symbol}
          </div>
          <div
            role="columnheader"
            className="px-5 py-2.5 text-caption uppercase tracking-wider text-muted-foreground"
          >
            {labels.timeframe}
          </div>
          <div
            role="columnheader"
            className="px-5 py-2.5 text-caption uppercase tracking-wider text-muted-foreground"
          >
            {labels.deployed}
          </div>
          <div
            role="columnheader"
            className="px-5 py-2.5 text-caption uppercase tracking-wider text-muted-foreground"
          >
            {labels.status}
          </div>
          <div role="columnheader" className="px-5 py-2.5" />
        </div>

        <div role="rowgroup" className="flex flex-col gap-2 px-2 py-2">
          {all.map((entry, i) => (
            <VersionDesktopCard
              key={entry.id}
              entry={entry}
              isLatest={i === 0}
              running={isEntryRunning?.(entry) ?? false}
              isRecentlyDeployed={entry.id === recentlyDeployedId}
              onHighlightEnd={onDeployHighlightEnd}
              labels={labels}
              rowAction={renderRowAction?.(entry)}
              versionBadge={renderVersionBadge?.(entry)}
              rowDetails={renderRowDetails?.(entry)}
              onOpenCode={() => onOpenCode?.(entry)}
              onBuild={() => onBuild?.(entry)}
              onOpenParams={() => onOpenParams?.(entry)}
              onShowLogs={onShowLogs ? () => onShowLogs(entry) : undefined}
              onUndeploy={() => onUndeployVersion?.(entry)}
              onNoteChange={onNoteChange ? (next) => onNoteChange(entry, next) : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

interface VersionRowProps {
  entry: PublishedDeployment
  isLatest: boolean
  running: boolean
  isRecentlyDeployed: boolean
  onHighlightEnd?: () => void
  labels: DeploymentTableLabels
  rowAction?: React.ReactNode
  versionBadge?: React.ReactNode
  onOpenCode: () => void
  onBuild: () => void
  onOpenParams: () => void
  onShowLogs?: () => void
  onUndeploy: () => void
  onNoteChange?: (note: string) => void
}

interface VersionDesktopCardProps extends VersionRowProps {
  rowDetails?: React.ReactNode
}

function VersionDesktopCard({
  entry,
  isLatest,
  running,
  isRecentlyDeployed,
  onHighlightEnd,
  labels,
  rowAction,
  versionBadge,
  rowDetails,
  onOpenCode,
  onBuild,
  onOpenParams,
  onShowLogs,
  onUndeploy,
  onNoteChange,
}: VersionDesktopCardProps) {
  const cardRef = useDeployHighlight(isRecentlyDeployed, onHighlightEnd) as React.RefObject<HTMLDivElement>

  return (
    <div
      ref={cardRef}
      role="row"
      className="rounded-lg border border-border bg-card shadow-sm overflow-hidden hover:bg-muted/30 transition-colors"
    >
      <div className={cn("grid items-center", DESKTOP_GRID_COLS)}>
        <div role="cell" className="px-5 py-2.5 min-w-0">
          <PublishedNoteEditor note={entry.note ?? ""} onChange={onNoteChange} />
        </div>
        <div role="cell" className="px-5 py-2.5 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "text-caption font-mono tabular-nums",
                isLatest ? "text-primary font-semibold" : "text-foreground",
              )}
            >
              {entry.ver}
            </span>
            {versionBadge}
          </div>
        </div>
        <div role="cell" className="px-5 py-2.5">
          <DeploymentSymbolPill symbol={entry.sym} symbols={entry.symbols} />
        </div>
        <div role="cell" className="px-5 py-2.5">
          <DeploymentTimeframePill timeframe={entry.tf} timeframes={entry.timeframes} />
        </div>
        <div
          role="cell"
          className="px-5 py-2.5 text-caption text-muted-foreground whitespace-nowrap"
        >
          {entry.date}
        </div>
        <div role="cell" className="px-5 py-2.5">
          <DeploymentStatusBadge running={running} />
        </div>
        <div role="cell" className="px-5 py-2.5 whitespace-nowrap">
          <div className="flex items-center justify-end gap-1.5">
            {rowAction}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 [&_svg]:size-3.5 text-muted-foreground hover:text-foreground"
                  aria-label={labels.rowActionsAriaLabel(entry)}
                >
                  <MoreVertical size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="v1 w-40">
                <DropdownMenuItem onSelect={onOpenCode} className="text-caption">
                  <FileCode2 size={12} strokeWidth={1.75} />
                  {labels.codeAction}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onOpenParams} className="text-caption">
                  <SlidersHorizontal size={12} strokeWidth={1.75} />
                  {labels.settingsAction}
                </DropdownMenuItem>
                {onShowLogs && (
                  <DropdownMenuItem onSelect={onShowLogs} className="text-caption">
                    <ScrollText size={12} strokeWidth={1.75} />
                    {labels.logsAction}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={onBuild} className="text-caption">
                  <Hammer size={12} strokeWidth={1.75} />
                  {labels.buildAction}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onUndeploy} className="text-caption">
                  <Trash2 size={12} strokeWidth={1.75} />
                  {labels.deleteAction}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {rowDetails ? (
        <div className="border-t border-border bg-muted/10 px-5 py-3">
          {rowDetails}
        </div>
      ) : null}
    </div>
  )
}

interface VersionMobileCardProps extends VersionRowProps {
  formatMobileDate?: (date: string) => string
  rowDetails?: React.ReactNode
}

function VersionMobileCard({
  entry,
  isLatest,
  running,
  isRecentlyDeployed,
  onHighlightEnd,
  labels,
  rowAction,
  versionBadge,
  rowDetails,
  onOpenCode,
  onBuild,
  onOpenParams,
  onShowLogs,
  onUndeploy,
  onNoteChange,
  formatMobileDate,
}: VersionMobileCardProps) {
  const cardRef = useDeployHighlight(isRecentlyDeployed, onHighlightEnd) as React.RefObject<HTMLButtonElement>
  const [actionsOpen, setActionsOpen] = useState(false)
  const note = entry.note ?? ""
  const hasNote = note.length > 0
  const mobileDate = formatMobileDate ? formatMobileDate(entry.date) : entry.date

  const closeActions = () => setActionsOpen(false)
  const handleCode = () => onOpenCode()
  const handleParams = () => onOpenParams()
  const handleLogs = () => {
    closeActions()
    onShowLogs?.()
  }
  const handleBuild = () => {
    closeActions()
    onBuild()
  }
  const handleUndeploy = () => {
    closeActions()
    onUndeploy()
  }

  return (
    <>
      <div className="flex flex-col">
      <button
        ref={cardRef}
        type="button"
        onClick={() => setActionsOpen(true)}
        aria-label={labels.rowActionsAriaLabel(entry)}
        className="w-full px-4 py-3 flex flex-col gap-1.5 text-left cursor-pointer hover:bg-muted/30 active:bg-muted/50 transition-colors"
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <span
              className={cn(
                "text-body font-mono tabular-nums",
                isLatest ? "text-primary font-semibold" : "text-foreground",
              )}
            >
              {entry.ver}
            </span>
            {versionBadge}
            <DeploymentSymbolPill
              symbol={entry.sym}
              symbols={entry.symbols}
              countSuffix=" pairs"
              plain
            />
            <DeploymentTimeframePill
              timeframe={entry.tf}
              timeframes={entry.timeframes}
              countSuffix=" TFs"
              plain
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {rowAction && (
              <span onClick={(e) => e.stopPropagation()}>{rowAction}</span>
            )}
            <DeploymentStatusBadge running={running} />
          </div>
        </div>

        <div className="flex items-start justify-between gap-3 w-full">
          <div className="min-w-0 flex-1">
            {hasNote ? (
              <span className="text-caption text-foreground line-clamp-2 break-words">
                {note}
              </span>
            ) : (
              <span className="text-caption text-muted-foreground italic inline-flex items-center gap-1.5">
                <Pencil size={11} strokeWidth={1.75} className="shrink-0" />
                Add a note…
              </span>
            )}
          </div>
          <span className="text-caption text-muted-foreground whitespace-nowrap tabular-nums shrink-0">
            {mobileDate}
          </span>
        </div>
      </button>
      {rowDetails ? (
        <div
          className="px-4 pb-3 -mt-1 bg-muted/10"
          onClick={(e) => e.stopPropagation()}
        >
          {rowDetails}
        </div>
      ) : null}
      </div>

      <Sheet open={actionsOpen} onOpenChange={setActionsOpen}>
        <SheetContent
          side="bottom"
          className="v1 bg-card border-border max-h-[85dvh] overflow-hidden flex flex-col p-0"
        >
          <SheetHeader className="shrink-0 px-4 pt-4 pb-3 border-b border-border">
            <SheetTitle className="text-section text-foreground flex items-baseline gap-2">
              {labels.mobileActionsTitle(entry)}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 min-h-0 overflow-y-auto">
            <PublishedNoteEditorContent
              note={note}
              onChange={onNoteChange}
              onClose={() => undefined}
              initialMode="view"
            />
          </div>

          <div className="shrink-0 border-t border-border">
            <div className="grid grid-cols-3 gap-2 px-4 py-3 border-b border-border">
              <Button
                variant="outline"
                onClick={handleCode}
                className="h-11 gap-1.5 text-caption"
              >
                <FileCode2 size={14} strokeWidth={1.75} />
                {labels.codeAction}
              </Button>
              <Button
                variant="outline"
                onClick={handleParams}
                className="h-11 gap-1.5 text-caption"
              >
                <SlidersHorizontal size={14} strokeWidth={1.75} />
                {labels.settingsAction}
              </Button>
              <Button
                variant="outline"
                onClick={handleBuild}
                className="h-11 gap-1.5 text-caption"
              >
                <Hammer size={14} strokeWidth={1.75} />
                {labels.buildAction}
              </Button>
            </div>
            <div className="pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              {onShowLogs && (
                <button
                  type="button"
                  onClick={handleLogs}
                  className="flex items-center gap-3 px-4 py-3.5 text-left text-body text-foreground hover:bg-muted/60 active:bg-muted transition-colors cursor-pointer w-full"
                >
                  <ScrollText
                    size={16}
                    strokeWidth={1.75}
                    className="shrink-0 text-muted-foreground"
                  />
                  {labels.logsAction}
                </button>
              )}
              <button
                type="button"
                onClick={handleUndeploy}
                className="flex items-center gap-3 px-4 py-3.5 text-left text-body text-foreground hover:bg-muted/60 active:bg-muted transition-colors cursor-pointer w-full"
              >
                <Trash2
                  size={16}
                  strokeWidth={1.75}
                  className="shrink-0 text-muted-foreground"
                />
                {labels.deleteAction}
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
